import { useState, useMemo, useRef, useEffect } from "react";
import { Search, X, Check } from "lucide-react";

/**
 * Escolher o parceiro digitando o nome, em vez de rolar um <select>.
 *
 * O dropdown nativo listava as centenas de parceiros da rede em ordem de
 * cadastro: achar "Auto Posto Silva" era rolar até encontrar, e quem estava com
 * pressa acabava mandando a ficha sem vínculo. Aqui digita-se parte do nome, do
 * CNPJ ou da cidade e a lista encolhe.
 *
 * Continua sendo um campo opcional: o botão "sem vínculo" existe e é explícito.
 */
export default function BuscaParceiro({ partners = [], value = "", onChange, variant = "crm", placeholder = "Digite o nome, CNPJ ou cidade..." }) {
  const [busca, setBusca] = useState("");
  const [aberto, setAberto] = useState(false);
  const boxRef = useRef(null);

  const selecionado = useMemo(() => partners.find((p) => p.id === value) || null, [partners, value]);

  // Clique fora fecha — senão a lista fica por cima do resto do formulário.
  useEffect(() => {
    const fora = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setAberto(false); };
    document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  }, []);

  const resultados = useMemo(() => {
    const t = busca.trim().toLowerCase();
    const base = partners;
    if (!t) return base.slice(0, 8);
    const num = t.replace(/\D/g, "");
    return base
      .filter((p) => {
        const alvo = `${p.name || ""} ${p.nomeFantasia || ""} ${p.razaoSocial || ""} ${p.city || p.cidade || ""}`.toLowerCase();
        const cnpj = String(p.cnpj || "").replace(/\D/g, "");
        return alvo.includes(t) || (num.length >= 3 && cnpj.includes(num));
      })
      .slice(0, 20);
  }, [partners, busca]);

  const pub = variant === "public";
  const inp = {
    width: "100%", padding: pub ? "11px 12px 11px 34px" : "8px 12px 8px 32px",
    borderRadius: pub ? 10 : 8, fontSize: pub ? 15 : 14, outline: "none", boxSizing: "border-box",
    fontFamily: "inherit",
    border: pub ? "1px solid #e2e8f0" : "1px solid rgba(128,128,128,0.08)",
    background: pub ? "#f8fafc" : "rgba(128,128,128,0.08)",
    color: pub ? "#334155" : "#555555",
  };

  if (selecionado) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", borderRadius: 8, border: "1.5px solid #7c3aed", background: "#f5f3ff" }}>
        <Check size={14} color="#6d28d9" style={{ flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#4c1d95", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {selecionado.name || selecionado.nomeFantasia || selecionado.razaoSocial}
          </div>
          {(selecionado.cnpj || selecionado.city || selecionado.cidade) && (
            <div style={{ fontSize: 10.5, color: "#7c3aed" }}>
              {[selecionado.cnpj, selecionado.city || selecionado.cidade].filter(Boolean).join(" · ")}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => { onChange(""); setBusca(""); }}
          title="Trocar parceiro"
          style={{ background: "none", border: "none", cursor: "pointer", color: "#7c3aed", display: "flex", flexShrink: 0 }}
        >
          <X size={15} />
        </button>
      </div>
    );
  }

  return (
    <div ref={boxRef} style={{ position: "relative" }}>
      <Search size={14} color="#94a3b8" style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
      <input
        style={inp}
        value={busca}
        onChange={(e) => { setBusca(e.target.value); setAberto(true); }}
        onFocus={() => setAberto(true)}
        placeholder={placeholder}
      />
      {aberto && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 40, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, boxShadow: "0 10px 25px rgba(0,0,0,0.12)", maxHeight: 260, overflowY: "auto" }}>
          {resultados.length === 0 ? (
            <div style={{ padding: "12px 14px", fontSize: 12, color: "#94a3b8" }}>
              Nenhum parceiro encontrado para "{busca}".
            </div>
          ) : (
            resultados.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => { onChange(p.id); setAberto(false); setBusca(""); }}
                style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 12px", border: "none", borderBottom: "1px solid #f1f5f9", background: "#fff", cursor: "pointer", fontFamily: "inherit" }}
              >
                <div style={{ fontSize: 12.5, fontWeight: 600, color: "#334155" }}>{p.name || p.nomeFantasia || p.razaoSocial}</div>
                {(p.cnpj || p.city || p.cidade) && (
                  <div style={{ fontSize: 10.5, color: "#94a3b8" }}>{[p.cnpj, p.city || p.cidade].filter(Boolean).join(" · ")}</div>
                )}
              </button>
            ))
          )}
          {!busca.trim() && partners.length > resultados.length && (
            <div style={{ padding: "8px 12px", fontSize: 10.5, color: "#94a3b8", background: "#f8fafc" }}>
              Mostrando {resultados.length} de {partners.length} — digite para filtrar.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
